import { useCallback, useEffect, useRef, useState } from 'react';

import { usePublish } from '@atlaskit/rovo-triggers/main';
import type { RovoContentBridgeSetupHandler } from '@atlassian/rovo-content-bridge-api/client';
import { EMPTY_CONVERSATION_ID } from '@atlassian/conversation-assistant-store/controllers/chat';
import { useUIStore } from '@atlassian/conversation-assistant-store/controllers/ui-store';
import { useSmartCreationChatStoreApi } from '@atlassian/conversation-assistant';
import { useConversationActionsStoreState } from '@atlassian/rovo-conversation-actions';

import { useToggleConversationAssistant } from '@confluence/conversation-assistant/entry-points/useToggleConversationAssistant.ts';

import { OpenRovoSidePanelCommand } from './commands/open-rovo-side-panel-command.ts';

export const useOpenRovoSidePanelHandler = (): RovoContentBridgeSetupHandler => {
	const { toggleConversationAssistant, isConversationAssistantSidebarActive } =
		useToggleConversationAssistant();
	const { streaming, setActiveMenu } = useUIStore();
	const { setSelectedConversation } = useSmartCreationChatStoreApi();
	const { actions } = useConversationActionsStoreState();
	const publishActions = usePublish('ai-mate-actions');
	const publishChat = usePublish('ai-mate');

	// Use a ref so the command handler always reads the latest sidebar state
	// without needing isConversationAssistantSidebarActive in useCallback deps
	// (which would cause handler re-registration and missed/duplicate events)
	const isSidebarActiveRef = useRef(isConversationAssistantSidebarActive);
	isSidebarActiveRef.current = isConversationAssistantSidebarActive;

	const [pendingActionId, setPendingActionId] = useState<string | undefined>(undefined);
	const [pendingPrefillPrompt, setPendingPrefillPrompt] = useState<string | undefined>(undefined);

	// Publish pending action once sidebar is open and actions are loaded
	useEffect(() => {
		if (pendingActionId && isConversationAssistantSidebarActive && actions.length > 0) {
			// Uses setTimeout to ensure the component has fully rendered
			// Not doing this results in a mixed state where the sidebar thinks it's in a nested action but is displaying the actions visible at the root	level
			const timeoutId = setTimeout(() => {
				publishActions({
					type: 'select-action',
					source: 'whiteboard-rovo-bridge',
					data: { actionId: pendingActionId },
				});
				setPendingActionId(undefined);
			}, 0);

			return () => clearTimeout(timeoutId);
		}
	}, [pendingActionId, isConversationAssistantSidebarActive, actions, publishActions]);

	// Publish pending prefill prompt once sidebar is open
	useEffect(() => {
		if (pendingPrefillPrompt && isConversationAssistantSidebarActive) {
			const timeoutId = setTimeout(() => {
				publishChat({
					type: 'insert-prompt',
					source: 'whiteboard-rovo-bridge',
					data: {
						prompt: pendingPrefillPrompt,
						overrideAutoSend: true,
					},
				});
				setPendingPrefillPrompt(undefined);
			}, 0);

			return () => clearTimeout(timeoutId);
		}
	}, [pendingPrefillPrompt, isConversationAssistantSidebarActive, publishChat]);

	return useCallback(
		(on) => {
			on(OpenRovoSidePanelCommand, ({ actionId, prefillPrompt }) => {
				// Don't set action if currently streaming
				if (!streaming && actionId) {
					setPendingActionId(actionId);
					setSelectedConversation({ conversationId: EMPTY_CONVERSATION_ID });
					setActiveMenu('none');
				}

				if (prefillPrompt) {
					setPendingPrefillPrompt(prefillPrompt);
				}

				// Use ref to read latest sidebar state without causing handler re-registration
				if (!isSidebarActiveRef.current) {
					toggleConversationAssistant('sidebar');
				}
			});
		},
		[streaming, toggleConversationAssistant, setSelectedConversation, setActiveMenu],
	);
};
